import { useConfig } from "@mimber/client/mimber-organization"
import { Spin, Result, Layout } from "@mimber/ui-web"
import React from "react"

const { Content } = Layout

export interface ConfigGateProps {
  children: React.ReactNode
}

export const ConfigGate: React.FunctionComponent<ConfigGateProps> = ({
  children,
}) => {
  const { data: configResponse, isLoading, error } = useConfig()
  const config = configResponse?.data

  // Show loading while configuration is loading
  if (isLoading) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--color-background)",
          }}
        >
          <div className="flex flex-col items-center text-center">
            <Spin size="large" data-testid="loading-spinner" />
            <p className="mt-4 text-muted-foreground">
              Loading configuration...
            </p>
          </div>
        </Content>
      </Layout>
    )
  }

  // Show configuration error
  if (error) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            background: "var(--color-background)",
          }}
        >
          <div className="max-w-md w-full">
            <Result
              status="error"
              title="Configuration Error"
              subTitle={
                error.message ||
                "Failed to load application configuration. Please try refreshing the page."
              }
              extra={[
                <button
                  key="refresh"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </button>,
              ]}
            />
          </div>
        </Content>
      </Layout>
    )
  }

  // Show maintenance banner if organization maintenance is enabled
  if (config?.mimberOrganizationMaintenance) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            background: "var(--color-background)",
          }}
        >
          <div className="max-w-md w-full">
            <Result
              status="warning"
              title="🚧 System Maintenance"
              subTitle="The Mimber Organization portal is currently under maintenance. Please check back later. We apologize for any inconvenience and appreciate your patience."
              extra={[
                <button
                  key="refresh"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  onClick={() => window.location.reload()}
                >
                  Check Again
                </button>,
              ]}
            />
          </div>
        </Content>
      </Layout>
    )
  }

  // Config loaded successfully and no maintenance mode - render children
  return <>{children}</>
}
